<script setup lang="ts">
// A pure dropdown atom: the same field recipe as [[Input]] (surface-raised fill,
// subtle strong border, radius, visible focus ring), for a native `<select>`.
// `v-model` compatible via `modelValue` + `update:modelValue`; the default slot
// carries the `<option>`s. It knows nothing of any feature — id, name, aria-*
// fall through as attrs. Styled entirely from SD-052 semantic tokens.
//
// Only the shared box is owned here; each consumer keeps its own padding/colour/
// width on its retained class, disjoint from these props, so adoption stays
// pixel-neutral without a cascade-order fight (the [[Surface]] pattern).
withDefaults(
  defineProps<{
    modelValue: string;
    disabled?: boolean;
  }>(),
  { disabled: false }
);

const emit = defineEmits<{ 'update:modelValue': [string] }>();

function onChange(event: Event): void {
  emit('update:modelValue', (event.target as HTMLSelectElement).value);
}
</script>

<template>
  <select class="select" :value="modelValue" :disabled="disabled" @change="onChange">
    <slot />
  </select>
</template>

<style scoped>
.select {
  font: inherit;
  font-size: var(--font-size-sm);
  background: var(--color-surface-raised);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
}

.select:focus-visible {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}

.select:disabled {
  cursor: not-allowed;
  color: var(--color-text-muted);
  background: var(--color-surface-sunken);
}
</style>
